import AppRouterConfig from 'routes/AppRouterConfig';
import { createTheme } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { APIProvider } from '@vis.gl/react-google-maps';
import ConfigProvider from 'antd/es/config-provider';
import vi_VN from 'antd/es/locale/vi_VN';
import theme from 'antd/es/theme';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import SplashScreen from 'components/SplashScreen';
import DOMPurify from 'dompurify';
import {
  appPrimaryColorSelector,
  darkModeSelector,
} from 'features/app-info/selectors';
import { setDarkMode } from 'features/app-info/slice';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import { Suspense, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import ScrollRestoration from 'components/ScrollRestoration';
import { darkThemeOptions, themeOptions } from 'styles/MuiTheme';
import { setAppFavicon } from 'utils/getAppLogo';

DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const App = () => {
  const dispatch = useAppDispatch();
  const appPrimaryColor = useAppSelector(appPrimaryColorSelector);
  const isDarkMode = useAppSelector(darkModeSelector);

  const MuiTheme = createTheme(isDarkMode ? darkThemeOptions : themeOptions);

  useEffect(() => {
    setAppFavicon();
    dispatch(setDarkMode(localStorage.getItem('darkMode') === 'true'));
  }, [dispatch]);

  return (
    <APIProvider apiKey={import.meta.env.VITE_APP_GM_API_KEY}>
      <ThemeProvider theme={MuiTheme}>
        <StyledEngineProvider injectFirst>
          <ConfigProvider
            locale={vi_VN}
            theme={{
              algorithm: isDarkMode
                ? theme.darkAlgorithm
                : theme.defaultAlgorithm,
              token: {
                colorPrimary: appPrimaryColor,
                borderRadius: 5,
                fontSize: 14,
              },
            }}
          >
            <Suspense
              fallback={
                <ScrollRestoration>
                  <SplashScreen fullScreen />
                </ScrollRestoration>
              }
            >
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                <RouterProvider router={AppRouterConfig} />
              </Worker>
            </Suspense>
          </ConfigProvider>
        </StyledEngineProvider>
      </ThemeProvider>
    </APIProvider>
  );
};

export default App;
